/**
 * @author: chenlv
 * @date: 2025/01/30
 * @description: CasualGenreProfile 页面专用 Tooltip Formatter 工具函数
 */
import moment from 'moment';
import { renderPieChartTooltip, renderBarChartTooltip } from '@intelligence/v3/components/ChartComponents/Tooltip';
import { createLineChartTooltipFormatter } from '@intelligence/v3/components/ChartComponents/Tooltip/components/LineChartTooltip';
import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { generateMetricKey, generateMetricRatioKey } from '@intelligence/v3/utils/metric';

import type { TDataSourceType, TMetricType } from '../../types';

/**
 * 格式化环比值
 * @param ratioValue 环比值（小数形式，如 0.5 表示 50%）
 * @returns 格式化后的环比对象
 */
const formatRatioValue = (
  ratioValue: number | null | undefined,
): { type: 'up' | 'down' | 'none'; value: string } | undefined => {
  if (ratioValue === null || ratioValue === undefined || Number.isNaN(ratioValue)) {
    return undefined;
  }

  const percentage = Math.abs(ratioValue * 100).toFixed(1);

  if (ratioValue > 0) {
    return { type: 'up', value: `${percentage}%` };
  }
  if (ratioValue < 0) {
    return { type: 'down', value: `${percentage}%` };
  }
  return { type: 'none', value: '0%' };
};

/**
 * 获取环比值格式化函数
 * @param dataSource 数据源
 * @param metric 指标类型
 * @returns 环比值格式化函数
 */
const getRatioFormatter = (dataSource: TDataSourceType, metric: TMetricType) => {
  return (payload: any): { type: 'up' | 'down' | 'none'; value: string } | undefined => {
    const metricKey = generateMetricKey({ dataSource, metric });
    const metricRatioKey = generateMetricRatioKey(metricKey);
    const ratioValue = payload?.[metricRatioKey] ?? null;
    return formatRatioValue(ratioValue);
  };
};

/**
 * 获取数据源图标URL
 * @param dataSource 数据源类型
 * @returns 数据源图标URL
 */
const getDataSourceIconUrl = (dataSource: string): string => {
  return getDataSourceIcon(dataSource) || '';
};

/**
 * 根据粒度创建X轴标签格式化函数
 * @param granularity 日期粒度
 * @returns X轴标签格式化函数
 */
const createXAxisLabelFormatter = (granularity?: IGranularity) => {
  return (axisValue: string): string => {
    if (!axisValue) return '';

    const date = moment(axisValue);
    if (!date.isValid()) return axisValue;

    // 月粒度：YYYY-MM 格式
    if (granularity === 'monthly') {
      return date.format('YYYY-MM');
    }
    // 日粒度：YYYY-MM-DD 格式
    if (granularity === 'daily') {
      return date.format('YYYY-MM-DD');
    }

    // 默认返回原始值
    return axisValue;
  };
};

/**
 * 创建饼状图 Tooltip Formatter
 * @param params 配置参数
 * @returns ECharts tooltip formatter 函数
 */
export const createPieChartTooltipFormatter = (params: {
  dataSource: TDataSourceType;
  metric: TMetricType;
  metricName: string;
  formatGenreName?: (name: string) => string;
}) => {
  const { dataSource, metricName, formatGenreName } = params;
  const dataSourceIcon = getDataSourceIconUrl(dataSource);

  return (echartsParams: any): string => {
    const param = Array.isArray(echartsParams) ? echartsParams[0] : echartsParams;
    if (!param) return '';

    const name = param.name || '';
    const value = param.value || 0;
    const percent = param.percent ?? null;
    const color = param.color || '#999';
    const payload = param.data?.payload || {};

    // 获取环比值
    const ratio = formatRatioValue(payload.ratio);

    return renderPieChartTooltip({
      metricName,
      dataItem: {
        color,
        name: formatGenreName?.(name) ?? name,
        value: formatKMBWithPlaceholder(value),
        percent: percent != null ? `${Number(percent.toFixed(2))}%` : '-',
        ratio,
      },
      dataSourceIcon,
      showDataSourceIcon: true,
    });
  };
};

/**
 * 创建柱状图 Tooltip Formatter
 * @param params 配置参数
 * @returns ECharts tooltip formatter 函数
 */
export const createBarChartTooltipFormatter = (params: {
  dataSource: TDataSourceType;
  metric: TMetricType;
  metricName: string;
  formatGenreName?: (name: string) => string;
}) => {
  const { dataSource, metric, metricName, formatGenreName } = params;
  const dataSourceIcon = getDataSourceIconUrl(dataSource);
  const ratioFormatter = getRatioFormatter(dataSource, metric);

  return (echartsParams: any): string => {
    const paramList = Array.isArray(echartsParams) ? echartsParams : [echartsParams];
    if (paramList.length === 0) return '';

    const dataList = paramList.map((param: any) => {
      const name = param.name || '';
      const value = param.value || 0;
      const color = param.color || '#999';
      const payload = param.data?.payload || {};

      return {
        color,
        name: formatGenreName?.(name) ?? name,
        value: formatKMBWithPlaceholder(value),
        dataSourceIcon,
        ratio: ratioFormatter(payload),
      };
    });

    return renderBarChartTooltip({
      metricName,
      dataList,
      showDataSourceIcon: true,
    });
  };
};

/**
 * 创建折线图 Tooltip Formatter
 * @param params 配置参数
 * @returns ECharts tooltip formatter 函数
 */
export const createGenreLineChartTooltipFormatter = (params: {
  dataSource: TDataSourceType;
  metric: TMetricType;
  metricName: string;
  formatGenreName?: (name: string) => string;
  granularity?: IGranularity;
}) => {
  const { dataSource, metric, metricName, formatGenreName, granularity } = params;
  const dataSourceIcon = getDataSourceIconUrl(dataSource);
  const ratioFormatter = getRatioFormatter(dataSource, metric);
  const xAxisLabelFormatter = createXAxisLabelFormatter(granularity);

  return createLineChartTooltipFormatter({
    metricName,
    seriesNameFormatter: formatGenreName,
    valueFormatter: formatKMBWithPlaceholder,
    xAxisLabelFormatter,
    ratioFormatter,
    dataSourceIcon,
    showDataSourceIcon: true,
  });
};

/**
 * 创建横向柱状图 Tooltip Formatter
 * @param params 配置参数
 * @returns ECharts tooltip formatter 函数
 */
export const createHorizontalBarChartTooltipFormatter = (params: {
  dataSource: TDataSourceType;
  metric: TMetricType;
  metricName: string;
  formatGenreName?: (name: string) => string;
}) => {
  // 横向柱状图使用与普通柱状图相同的formatter
  return createBarChartTooltipFormatter(params);
};

export default {
  createPieChartTooltipFormatter,
  createBarChartTooltipFormatter,
  createGenreLineChartTooltipFormatter,
  createHorizontalBarChartTooltipFormatter,
};
